import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { LogIn, LogOut, Clock, Coffee, KeyRound, FolderKanban, Loader2, MapPin, Wrench } from 'lucide-react';
import { parseServerDate, fmtTime } from '../../utils/dateTime';

const formatElapsed = (startIso) => {
  if (!startIso) return '00:00:00';
  const start = parseServerDate(startIso);
  if (!start) return '00:00:00';
  const diff = Math.max(0, Date.now() - start.getTime());
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const getGPSPosition = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => resolve(null),
      { timeout: 6000, enableHighAccuracy: true },
    );
  });

const WorkerToday = () => {
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [pulse, setPulse] = useState(0); // forces re-render every second for live timers

  // Clock-in form
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedMachine, setSelectedMachine] = useState('');
  const [comment, setComment] = useState('');
  const [pinCode, setPinCode] = useState('');
  const [hasPin, setHasPin] = useState(false);

  // Clock-out modal state
  const [outEntry, setOutEntry] = useState(null);
  const [outBreak, setOutBreak] = useState('0');
  const [outComment, setOutComment] = useState('');
  const [outPin, setOutPin] = useState('');
  const [outMachineHours, setOutMachineHours] = useState('');  // valfri separat maskintid
  // Fas 5 (#8): tidig utstämpling — anledning
  const [earlyReason, setEarlyReason] = useState('');
  const [earlyDetails, setEarlyDetails] = useState(null);  // {early_by_min, scheduled_end, message}

  const fetchStatus = useCallback(async () => {
    try {
      const [statusRes, projRes, pinRes, machRes] = await Promise.all([
        api.getClockStatus(),
        api.getProjects(),
        api.getMyPinStatus(),
        api.getMachines().catch(() => ({ data: [] })),
      ]);
      setEntries(statusRes.data?.entries || []);
      setProjects(projRes.data || []);
      setHasPin(pinRes.data?.has_pin || false);
      setMachines(machRes.data || []);
    } catch (err) {
      console.error('Failed to fetch status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Tick for live elapsed timers
  useEffect(() => {
    if (entries.length === 0) return;
    const t = setInterval(() => setPulse((p) => p + 1), 1000);
    return () => clearInterval(t);
  }, [entries.length]);

  const projectName = (id) => projects.find((p) => p.id === id)?.name || 'Inget projekt';
  const machineName = (id) => {
    const m = machines.find((x) => x.id === id);
    if (!m) return null;
    return `${m.name}${m.registration_no ? ` (${m.registration_no})` : ''}`;
  };

  const handleClockIn = async () => {
    if (!selectedProject) { toast.error('Välj ett projekt först'); return; }
    if (hasPin && !pinCode) { toast.error('Ange din pinkod'); return; }
    setActing(true);
    try {
      const gps = await getGPSPosition();
      if (!gps) {
        toast.error('Vi behöver din GPS-position för att kontrollera arbetsplatsen. Tillåt platsåtkomst.');
        setActing(false);
        return;
      }
      await api.clockIn({
        project_id: selectedProject,
        machine_id: selectedMachine || null,
        comment: comment || null,
        pin_code: pinCode || null,
        latitude: gps.latitude,
        longitude: gps.longitude,
      });
      toast.success(`Instämplad på ${projectName(selectedProject)}!`);
      setSelectedProject('');
      setSelectedMachine('');
      setComment('');
      setPinCode('');
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte stämpla in');
    } finally {
      setActing(false);
    }
  };

  const openClockOut = (entry) => {
    setOutEntry(entry);
    setOutBreak('0');
    setOutComment('');
    setOutPin('');
    setOutMachineHours('');
    setEarlyReason('');
    setEarlyDetails(null);
  };

  const handleClockOut = async () => {
    if (!outEntry) return;
    if (hasPin && !outPin) { toast.error('Ange din pinkod'); return; }
    if (earlyDetails && !earlyReason.trim()) { toast.error('Ange en anledning för tidig utstämpling'); return; }
    setActing(true);
    try {
      // GPS is optional on clock-out — we send if available but don't block.
      const gps = await getGPSPosition();
      await api.clockOut({
        entry_id: outEntry.id,
        break_minutes: parseInt(outBreak) || 0,
        comment: outComment || null,
        pin_code: outPin || null,
        early_reason: earlyReason || null,
        machine_hours: outEntry.machine_id && outMachineHours !== ''
          ? Math.max(0, parseFloat(outMachineHours.replace(',', '.'))) || 0
          : null,
        ...(gps && { latitude: gps.latitude, longitude: gps.longitude }),
      });
      toast.success('Utstämplad!');
      setOutEntry(null);
      setEarlyReason('');
      setEarlyDetails(null);
      fetchStatus();
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Fas 5 (#8): Tidig utstämpling → visa formulär för anledning
      if (detail && typeof detail === 'object' && detail.code === 'early_clockout_needs_reason') {
        setEarlyDetails({
          early_by_min: detail.early_by_min,
          scheduled_end: detail.scheduled_end,
          message: detail.message,
        });
        toast.warning(detail.message || 'Ange en anledning för tidig utstämpling');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Kunde inte stämpla ut');
      }
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-6 h-6 text-[#E8613A] animate-spin" />
      </div>
    );
  }

  // Projects available for clock-in (exclude those already clocked in)
  const activeProjectIds = new Set(entries.filter((e) => e.project_id).map((e) => e.project_id));
  const availableProjects = projects
    .filter((p) => p.status === 'active')
    .filter((p) => !activeProjectIds.has(p.id));

  return (
    <div className="space-y-5 pb-20" data-testid="worker-today-page">
      {/* Header status */}
      <div
        className={`rounded-xl p-5 text-center ${
          entries.length > 0
            ? 'bg-green-50 dark:bg-green-950/30 border-2 border-green-300 dark:border-green-700'
            : 'bg-card border border-border'
        }`}
        data-testid="clock-status-card"
      >
        <div
          className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-2 ${
            entries.length > 0 ? 'bg-green-100 dark:bg-green-900/50' : 'bg-muted'
          }`}
        >
          <Clock size={22} className={entries.length > 0 ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'} />
        </div>
        <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
          {entries.length === 0
            ? 'Utstämplad'
            : entries.length === 1
            ? 'Instämplad på 1 projekt'
            : `Instämplad på ${entries.length} projekt`}
        </p>
      </div>

      {/* Active entries */}
      {entries.length > 0 && (
        <div className="space-y-3" data-testid="active-entries-list">
          {entries.map((e) => (
            <div
              key={e.id}
              className="bg-card border border-border rounded-xl p-4 flex items-center justify-between gap-3"
              data-testid={`active-entry-${e.id}`}
            >
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                  <FolderKanban size={12} /> {projectName(e.project_id)}
                </p>
                <p className="text-2xl font-bold text-foreground font-mono tracking-tight mt-0.5" data-testid={`elapsed-${e.id}`}>
                  {formatElapsed(e.clock_in)}
                </p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Sedan {fmtTime(e.clock_in)}
                  {e.location && <> · <MapPin size={9} className="inline" /> GPS</>}
                </p>
              </div>
              <Button
                onClick={() => openClockOut(e)}
                className="bg-destructive hover:bg-destructive/90 text-white h-10 px-4 rounded-full"
                data-testid={`clock-out-btn-${e.id}`}
              >
                <LogOut size={14} className="mr-1.5" /> Stämpla ut
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Clock-in form (always visible — supports multi-project) */}
      <div className="bg-card border border-border rounded-xl p-5 space-y-4" data-testid="clock-in-form">
        <div>
          <p className="text-sm font-bold text-foreground">
            {entries.length > 0 ? 'Stämpla in på ytterligare ett projekt' : 'Stämpla in'}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">Du behöver vara på arbetsplatsen (GPS verifieras)</p>
        </div>

        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Projekt</Label>
          <select
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            className="w-full h-11 px-3 border border-input rounded-lg text-sm bg-background text-foreground mt-1"
            data-testid="clock-project-select"
          >
            <option value="">Välj projekt...</option>
            {availableProjects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          {availableProjects.length === 0 && entries.length > 0 && (
            <p className="text-[11px] text-muted-foreground mt-1.5">Du är redan instämplad på alla aktiva projekt.</p>
          )}
        </div>

        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
            <Wrench size={11} /> Maskin (valfri)
          </Label>
          <select
            value={selectedMachine}
            onChange={(e) => setSelectedMachine(e.target.value)}
            className="w-full h-11 px-3 border border-input rounded-lg text-sm bg-background text-foreground mt-1"
            data-testid="clock-machine-select"
          >
            <option value="">Ingen maskin</option>
            {machines.map((m) => (
              <option key={m.id} value={m.id}>{m.name}{m.registration_no ? ` (${m.registration_no})` : ''}</option>
            ))}
          </select>
          {selectedMachine && (
            <p className="text-[11px] text-[#E8613A] mt-1.5 font-medium">
              Maskintimmar registreras automatiskt vid utstämpling.
            </p>
          )}
        </div>

        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Kommentar (valfri)</Label>
          <Input
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="T.ex. Grävning vid uppfart"
            className="h-11 mt-1"
            data-testid="clock-comment"
          />
        </div>

        {hasPin && (
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
              <KeyRound size={12} /> Pinkod
            </Label>
            <Input
              type="password" maxLength={4} inputMode="numeric"
              value={pinCode}
              onChange={(e) => setPinCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="4-siffrig kod"
              className="h-11 mt-1 text-center text-lg tracking-[0.3em] font-mono"
              data-testid="clock-pin-input"
            />
          </div>
        )}

        <Button
          onClick={handleClockIn}
          disabled={acting || !selectedProject || availableProjects.length === 0}
          className="w-full h-14 text-lg bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl"
          data-testid="clock-in-btn"
        >
          <LogIn size={22} className="mr-2" /> {acting ? 'Stämplar in...' : 'Stämpla in'}
        </Button>
      </div>

      {/* Clock-out modal */}
      {outEntry && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center px-4 pb-4 sm:pb-0"
          onClick={() => !acting && setOutEntry(null)}
          data-testid="clock-out-modal"
        >
          <div
            className="w-full max-w-md bg-card rounded-2xl border border-border shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[#1A1A1A] text-white px-5 py-4">
              <p className="text-xs text-white/60">Stämpla ut från</p>
              <p className="text-base font-bold">{projectName(outEntry.project_id)}</p>
              {outEntry.machine_id && machineName(outEntry.machine_id) && (
                <p className="text-[11px] text-[#E8613A] mt-0.5 flex items-center gap-1">
                  <Wrench size={10} /> {machineName(outEntry.machine_id)}
                </p>
              )}
              <p className="text-xs text-white/70 font-mono mt-0.5">{formatElapsed(outEntry.clock_in)}</p>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                  <Coffee size={12} /> Rast (minuter)
                </Label>
                <Input
                  type="number" value={outBreak}
                  onChange={(e) => setOutBreak(e.target.value)}
                  className="h-11 mt-1"
                  data-testid="break-minutes"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Kommentar</Label>
                <Input
                  value={outComment}
                  onChange={(e) => setOutComment(e.target.value)}
                  placeholder="T.ex. Klart för idag"
                  className="h-11 mt-1"
                  data-testid="clock-out-comment"
                />
              </div>

              {/* Maskintid – syns endast om worker valde en maskin vid instämpling */}
              {outEntry.machine_id && (
                <div data-testid="machine-hours-block">
                  <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                    <Wrench size={11} /> Maskintimmar (valfri)
                  </Label>
                  <Input
                    type="number"
                    step="0.25"
                    min="0"
                    value={outMachineHours}
                    onChange={(e) => setOutMachineHours(e.target.value)}
                    placeholder={`Auto: ${(Math.max(0, (Date.now() - (parseServerDate(outEntry.clock_in)?.getTime() || Date.now())) / 3600000 - (parseInt(outBreak) || 0) / 60)).toFixed(2)} h`}
                    className="h-11 mt-1"
                    data-testid="clock-out-machine-hours"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1.5 leading-snug">
                    Om maskinen kördes kortare/längre än din arbetstid — ange antalet timmar den var igång.
                    Lämnas fältet tomt loggas maskintimmarna lika med din arbetstid.
                  </p>
                </div>
              )}
              {hasPin && (
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                    <KeyRound size={12} /> Pinkod
                  </Label>
                  <Input
                    type="password" maxLength={4} inputMode="numeric"
                    value={outPin}
                    onChange={(e) => setOutPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    className="h-11 mt-1 text-center text-lg tracking-[0.3em] font-mono"
                    data-testid="clock-out-pin-input"
                  />
                </div>
              )}

              {/* Fas 5 (#8): visas endast om servern flaggat att vi stämplar ut för tidigt */}
              {earlyDetails && (
                <div className="mt-2 p-3 rounded-lg bg-orange-950/40 border border-orange-500/40" data-testid="early-clockout-form">
                  <p className="text-xs font-semibold text-orange-300 mb-1">
                    Du stämplar ut {earlyDetails.early_by_min} min för tidigt
                  </p>
                  <p className="text-[11px] text-white/70 mb-2 leading-snug">
                    Ange en kort anledning så admin kan se varför. AI hjälper till att sortera anledningen.
                  </p>
                  <textarea
                    value={earlyReason}
                    onChange={(e) => setEarlyReason(e.target.value)}
                    placeholder="T.ex. Jobbet blev klart i förtid, sjukanmälan, materialbrist…"
                    rows={2}
                    className="w-full text-sm px-3 py-2 rounded-md bg-black/40 border border-orange-500/30 text-white placeholder-white/40 resize-none focus:outline-none focus:border-orange-400"
                    data-testid="early-clockout-reason"
                    required
                  />
                </div>
              )}
            </div>
            <div className="px-5 pb-5 flex gap-2">
              <Button
                variant="outline"
                onClick={() => setOutEntry(null)}
                disabled={acting}
                className="flex-1 h-11"
              >
                Avbryt
              </Button>
              <Button
                onClick={handleClockOut}
                disabled={acting}
                className="flex-1 h-11 bg-destructive hover:bg-destructive/90 text-white font-bold"
                data-testid="clock-out-confirm-btn"
              >
                {acting ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Stämplar ut...</> : <><LogOut size={14} className="mr-1.5" /> Stämpla ut</>}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden span to use pulse and avoid linter warning */}
      <span className="hidden" aria-hidden="true">{pulse}</span>
    </div>
  );
};

export default WorkerToday;
